import React, { useState, useEffect, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import './MicVolumeRing.css';

interface MicVolumeRingProps {
    volumeLevel: number;
    isActive?: boolean;
    size?: 'sm' | 'md' | 'lg';
    showLabel?: boolean;
    label?: string;
    className?: string;
}

const MicVolumeRing: React.FC<MicVolumeRingProps> = ({
    volumeLevel,
    isActive = true,
    size = 'md',
    showLabel = true,
    label = 'Microphone Level',
    className = ''
}) => {
    const [smoothedVolume, setSmoothedVolume] = useState(0);
    const previousVolumeRef = useRef(0);
    const animationFrameRef = useRef<number>();

    // Smoothing function using exponential moving average
    useEffect(() => {
        const smoothingFactor = 0.3; // Adjust for more/less smoothing
        const smoothed = previousVolumeRef.current * (1 - smoothingFactor) + volumeLevel * smoothingFactor;
        previousVolumeRef.current = smoothed;
        
        animationFrameRef.current = requestAnimationFrame(() => {
            setSmoothedVolume(smoothed);
        });

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [volumeLevel]);

    // Calculate ring properties based on size
    const getRingSize = () => {
        switch (size) {
            case 'sm': return { outerRadius: 30, innerRadius: 20, strokeWidth: 4 };
            case 'lg': return { outerRadius: 60, innerRadius: 40, strokeWidth: 8 };
            default: return { outerRadius: 45, innerRadius: 30, strokeWidth: 6 };
        }
    };

    const { outerRadius, innerRadius, strokeWidth } = getRingSize();
    const centerX = outerRadius + strokeWidth;
    const centerY = outerRadius + strokeWidth;
    const circumference = 2 * Math.PI * innerRadius;
    
    // Calculate the fill percentage with a minimum threshold
    const minThreshold = 0.05; // 5% minimum to show any activity
    const fillPercentage = Math.min(1, Math.max(0, (smoothedVolume - minThreshold) / (1 - minThreshold)));
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference * (1 - fillPercentage);

    // Determine color based on activity level
    const getRingColor = () => {
        if (!isActive) return '#6c757d'; // Gray when inactive
        if (fillPercentage > 0.4) return '#28a745'; // Green for high volume
        if (fillPercentage > 0.15) return '#ffc107'; // Yellow for medium volume
        return '#dc3545'; // Red for low volume
    };

    return (
        <Container className={`mic-volume-ring-container ${className}`} fluid>
            <Row className="justify-content-center">
                <Col xs="auto">
                    <div className="mic-volume-ring-wrapper">
                        <svg
                            width={centerX * 2}
                            height={centerY * 2}
                            className="mic-volume-ring-svg"
                            role="progressbar"
                            aria-valuenow={Math.round(fillPercentage * 100)}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={label}
                        >
                            {/* Background ring */}
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r={innerRadius}
                                fill="none"
                                stroke="#e9ecef"
                                strokeWidth={strokeWidth}
                                className="mic-volume-ring-background"
                            />
                            
                            {/* Volume fill ring */}
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r={innerRadius}
                                fill="none"
                                stroke={getRingColor()}
                                strokeWidth={strokeWidth}
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={strokeDashoffset}
                                strokeLinecap="round"
                                className="mic-volume-ring-fill"
                                style={{
                                    transform: 'rotate(-90deg)',
                                    transformOrigin: `${centerX}px ${centerY}px`
                                }}
                            />
                            
                            {/* Center dot for visual appeal */}
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r={innerRadius * 0.3}
                                fill={getRingColor()}
                                className="mic-volume-ring-center"
                            />
                        </svg>
                        
                        {showLabel && (
                            <div className="mic-volume-ring-label">
                                {label}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default MicVolumeRing; 